import {ICellRendererParams, MenuItemDef} from '@ag-grid-community/core';
import React, {ReactElement} from 'react';
import {GeoJsonPropertiesWazeIncident} from '../../../../interfaces/GeoJsonSources/GeoJsonPropertiesWaze';
import {WAZE_ALERT_ITEM_SUB_TYPES} from '../../../../interfaces/WazeAlertItemStatus';
import {MapFilters} from '../../../../scenes/MapScene/reducers/filterAndSearchReducer';
import {
    convertWazeTypeAndSubtypeToTranslatedEventType,
    translateFrontOfficeEventStatus,
} from '../../../../services/Utilities';
import {getWazeIcon} from '../../../../services/WazeIconService';
import AgGrid from '../../../AgGrid';
import {TimeCellRenderer} from '../../../AgGrid/components/DateCellRenderer';
import {MessageAgeCellRenderer} from '../../../AgGrid/components/MessageAgeCellRenderer';
import {FilterSettings} from '../../../AgGrid/Props';
import {Props} from './Props';
import {State} from './State';
import {FrontOfficeEventStatus} from '../../../../constants';
import {WazeFeature} from '../../../../interfaces/WazeFeature';
import {handleRowClick} from '../TableService';

class OverviewAllWazeAlertIncident extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            filters: null,
        };
    }

    componentDidMount(): void {
        this.updateGridFilters(this.props.mapFilter);
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        const {mapFilter} = this.props;

        if (mapFilter.filterTimeStamp !== prevProps.mapFilter.filterTimeStamp) {
            this.updateGridFilters(mapFilter);
        }
    }

    render(): ReactElement {
        const {data, wazeDataTypes, showRecordIds, suppressRowClickSelection} = this.props;
        const {filters} = this.state;

        return (
            <AgGrid<WazeFeature>
                columns={[
                    {
                        valueGetter: (item) => item.data.properties.id as keyof WazeFeature,
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: [true, false],
                        },
                        hide: !showRecordIds,
                    },
                    {
                        cellRenderer: (columnData: ICellRendererParams) => {
                            return <img src={`/assets/legend-icons/${getWazeIcon(columnData.value.type, columnData.value.subType)}.svg`} alt="Icoon" />;
                        },
                        valueGetter: (item) => ({type: item.data.properties.type, subType: item.data.properties.subType}),
                        headerName: '',
                        maxWidth: 50,
                        resizable: false,
                    },
                    {
                        cellRenderer: (columnData: ICellRendererParams) =>
                            convertWazeTypeAndSubtypeToTranslatedEventType(
                                columnData.data.properties.type,
                                columnData.data.properties.subType,
                            ),
                        valueGetter: (item) => item.data.properties.subType,
                        headerName: 'Type',
                    },
                    {
                        valueGetter: (item) => `${item.data.properties.address || ''} ${item.data.properties.city || ''}`,
                        headerName: 'Locatie (Waze)',
                    },
                    {
                        headerName: 'Locatie (NDW)',
                        valueGetter: (columData) => {
                            return `${columData.data.properties.ndwRoadInformation}`;
                        },
                    },
                    {
                        valueGetter: (item) => item.data.properties.overriddenRoadInformation,
                        headerName: 'Locatie (WVL)',
                    },
                    {
                        cellRenderer: (columnData: ICellRendererParams) =>
                            translateFrontOfficeEventStatus(columnData.value),
                        valueGetter: (item) => item.data.properties.status,
                        headerName: 'Status',
                    },
                    {
                        cellRenderer: TimeCellRenderer,
                        valueGetter: (item) => item.data.properties.startedAt,
                        headerName: 'Starttijd',
                        minWidth: 130,
                        sort: 'desc',
                    },
                    {
                        cellRenderer: MessageAgeCellRenderer,
                        valueGetter: (item) => item.data.properties.startedAt,
                        headerName: 'Duur incident',
                    },
                    {
                        valueGetter: (item) => item.data.properties.confirmedCount || 0,
                        headerName: 'Bevestigingen',
                        maxWidth: 130,
                    }
                ]}
                data={data}
                enableColResize={true}
                enableFilter={true}
                enableSorting={true}
                filters={filters ? filters : undefined}
                rowSelection={'single'}
                suppressScrollOnNewData={true}
                onRowClicked={(item) => handleRowClick(this.props, item)}
                autoRedrawTableColumns={true}
                suppressRowClickSelection={suppressRowClickSelection}
                getContextMenuItems={(params) => {
                    if (!params.node) {
                        return [];
                    }

                    const rowData: {id: string, properties: GeoJsonPropertiesWazeIncident} = params.node.data;
                    const id = rowData.id;
                    const status = rowData.properties.status;
                    const subType = rowData.properties.overriddenType as WAZE_ALERT_ITEM_SUB_TYPES;

                    const subMenuItemsType: MenuItemDef[] = wazeDataTypes.map<MenuItemDef>((wazeDataType) => {
                        return {
                            action: () => this.handleContextMenuChange(id, status, wazeDataType.subType),
                            icon: `<img src="${wazeDataType.icon}" alt="Icoon" style="height: 20px; width: 20px;"/>`,
                            name: wazeDataType.name,
                        };
                    });

                    return [
                        {
                            disabled: true,
                            icon: `<img src="/assets/legend-icons/ndw-${rowData.properties.id}-25.svg" alt="Icoon"/>`,
                            name: `Melding weggebruiker: ${rowData.properties.address}, ${rowData.properties.city}`,
                        },
                        {
                            disabled: true,
                            name: `Huidige status: ${translateFrontOfficeEventStatus(status)}`,
                        },
                        {
                            name: 'Status aanpassen',
                            subMenu: [
                                {
                                    disabled: true,
                                    icon: status === 'NONE' ? '<i class="fas fa-check" style="color: #28a745"/>' : '',
                                    name: 'Niet beoordeeld',
                                },
                                {
                                    action: () =>
                                        this.handleContextMenuChange(id, FrontOfficeEventStatus.noAction, subType),
                                    icon:
                                        status === 'NO_ACTION'
                                            ? '<i class="fas fa-check" style="color: #28a745"/>'
                                            : '',
                                    name: 'Niets aan de hand',
                                },
                                {
                                    action: () =>
                                        this.handleContextMenuChange(id, FrontOfficeEventStatus.selfAction, subType),
                                    icon:
                                        status === 'SELF_ACTION'
                                            ? '<i class="fas fa-check" style="color: #28a745"/>'
                                            : '',
                                    name: 'Zelf laten oplossen',
                                },
                                {
                                    action: () =>
                                        this.handleContextMenuChange(id, FrontOfficeEventStatus.knowMessage, subType),
                                    icon:
                                        status === 'KNOW_MESSAGE'
                                            ? '<i class="fas fa-check" style="color: #28a745"/>'
                                            : '',
                                    name: 'Reeds bekend',
                                },
                                {
                                    action: () =>
                                        this.handleContextMenuChange(id, FrontOfficeEventStatus.startAction, subType),
                                    icon:
                                        status === 'START_ACTION'
                                            ? '<i class="fas fa-check" style="color: #28a745"/>'
                                            : '',
                                    name: 'IM Opstarten',
                                },
                            ],
                        },
                        {
                            name: 'Type aanpassen',
                            subMenu: subMenuItemsType,
                        },
                    ];
                }}
            />
        );
    }

    private updateGridFilters = (mapFilters: MapFilters) => {
        const filters: FilterSettings<WazeFeature> = {
            filters: [],
            identifier: mapFilters.filterTimeStamp,
        };

        if (mapFilters.wazeAlertStatusSet) {
            filters.filters.push({
                field: 'properties.status' as keyof WazeFeature,
                value: ['NONE'],
            });
        }
        if (mapFilters.wazeAlertNdwKnown) {
            filters.filters.push({
                field: 'properties.ndw' as keyof WazeFeature,
                value: ['false'],
            });
        }

        this.setState({
            filters,
        });
    };

    private readonly handleContextMenuChange = (
        id: string,
        status: FrontOfficeEventStatus,
        subType: WAZE_ALERT_ITEM_SUB_TYPES,
    ) => {
        this.props.onSaveWazeAlertData(id, '', status, subType);
    };
}

export default OverviewAllWazeAlertIncident;
